import { useState, useMemo } from 'react';
import './LeadListView.css';

const TERMINAL_STATUSES = ['CONVERTED', 'DROPPED'];

function formatAum(val) {
  if (!val) return '—';
  const l = val / 100000;
  if (l >= 100) return `₹${(l / 100).toFixed(1)}Cr`;
  return `₹${l % 1 === 0 ? l : l.toFixed(1)}L`;
}

const SOURCE_LABELS = {
  REFERRAL: 'Referral', EVENT: 'Event', WALK_IN: 'Walk-in',
  COLD_CALL: 'Cold call', DIGITAL: 'Digital', OTHER: 'Other'
};

const STATUS_LABELS = {
  NEW: 'Just Discovered', CALLING: 'Already Talking', ENGAGED: 'Actively Engaged',
  MEETING_SCHEDULED: 'Meeting Lined Up', QUALIFIED: 'Ready to Invest',
  CONVERTED: 'Converted', DROPPED: 'Dropped'
};

const STATUS_COLORS = {
  NEW: '#8aaab5', CALLING: '#0090c1', ENGAGED: '#f0a500',
  MEETING_SCHEDULED: '#1aaa6e', QUALIFIED: '#004358',
  CONVERTED: '#2e7d32', DROPPED: '#b91c1c'
};

const INTEREST_LABELS = { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High' };

const ALL_SOURCES = ['REFERRAL', 'EVENT', 'WALK_IN', 'COLD_CALL', 'DIGITAL', 'OTHER'];
const ACTIVE_STATUSES = ['NEW', 'CALLING', 'ENGAGED', 'MEETING_SCHEDULED', 'QUALIFIED'];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function isOverdue(lead) {
  const now = Date.now();
  if (lead.next_follow_up_at && new Date(lead.next_follow_up_at) < now) return true;
  if (!lead.last_contacted_at && (now - new Date(lead.created_at)) / 86400000 > 3) return true;
  if (lead.last_contacted_at && !lead.next_follow_up_at &&
    (now - new Date(lead.last_contacted_at)) / 86400000 > 3) return true;
  return false;
}

export default function LeadListView({ leads, search, onCardClick, quickFilter }) {
  const [showClosed, setShowClosed] = useState(false);
  const [sourceFilter, setSourceFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [interestFilter, setInterestFilter] = useState([]);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  function toggleSource(s) {
    setSourceFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  }

  function toggleStatus(s) {
    setStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  }

  function toggleInterest(i) {
    setInterestFilter(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function SortIcon({ col }) {
    if (sortKey !== col) return <span className="llv__sort-icon llv__sort-icon--idle">↕</span>;
    return <span className="llv__sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  const filtered = useMemo(() => {
    let rows = leads;

    if (!showClosed) {
      rows = rows.filter(l => !TERMINAL_STATUSES.includes(l.status));
    }

    if (quickFilter === 'overdue') {
      rows = rows.filter(isOverdue);
    } else if (quickFilter === 'hot') {
      rows = rows.filter(l => (l.hot_score || 0) > 70);
    }

    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(l =>
        (l.name || '').toLowerCase().includes(q) ||
        (l.phone || '').toLowerCase().includes(q)
      );
    }

    if (sourceFilter.length > 0) {
      rows = rows.filter(l => sourceFilter.includes(l.source));
    }

    if (statusFilter.length > 0) {
      rows = rows.filter(l => statusFilter.includes(l.status));
    }

    if (interestFilter.length > 0) {
      rows = rows.filter(l => interestFilter.includes(l.interest_level));
    }

    rows = [...rows].sort((a, b) => {
      let av = a[sortKey] ?? '';
      let bv = b[sortKey] ?? '';
      if (sortKey === 'hot_score') { av = a.hot_score || 0; bv = b.hot_score || 0; }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [leads, showClosed, quickFilter, search, sourceFilter, statusFilter, interestFilter, sortKey, sortDir]);

  return (
    <div className="llv__wrap">
      <div className="llv__filter-bar">
        <div className="llv__filter-group">
          {ALL_SOURCES.map(s => (
            <button
              key={s}
              className={`llv__filter-chip ${sourceFilter.includes(s) ? 'llv__filter-chip--active' : ''}`}
              onClick={() => toggleSource(s)}
            >
              {SOURCE_LABELS[s]}
            </button>
          ))}
        </div>

        <div className="llv__filter-group">
          {ACTIVE_STATUSES.map(s => (
            <button
              key={s}
              className={`llv__filter-chip ${statusFilter.includes(s) ? 'llv__filter-chip--active' : ''}`}
              onClick={() => toggleStatus(s)}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        <div className="llv__filter-group">
          {['LOW', 'MEDIUM', 'HIGH'].map(i => (
            <button
              key={i}
              className={`llv__filter-chip ${interestFilter.includes(i) ? 'llv__filter-chip--active' : ''}`}
              onClick={() => toggleInterest(i)}
            >
              {INTEREST_LABELS[i]} interest
            </button>
          ))}
        </div>

        <label className="llv__toggle-closed">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={e => setShowClosed(e.target.checked)}
          />
          Show closed
        </label>
      </div>

      <div className="llv__table-wrap">
        <table className="llv__table">
          <thead>
            <tr>
              <th className="llv__th llv__th--sortable" onClick={() => handleSort('name')}>
                Name <SortIcon col="name" />
              </th>
              <th className="llv__th">Phone</th>
              <th className="llv__th llv__th--sortable" onClick={() => handleSort('source')}>
                Source <SortIcon col="source" />
              </th>
              <th className="llv__th llv__th--sortable" onClick={() => handleSort('status')}>
                Status <SortIcon col="status" />
              </th>
              <th className="llv__th llv__th--sortable" onClick={() => handleSort('interest_level')}>
                Interest <SortIcon col="interest_level" />
              </th>
              <th className="llv__th llv__th--sortable" onClick={() => handleSort('estimated_aum')}>
                Est. AUM <SortIcon col="estimated_aum" />
              </th>
              <th className="llv__th llv__th--sortable" onClick={() => handleSort('hot_score')}>
                Score <SortIcon col="hot_score" />
              </th>
              <th className="llv__th llv__th--sortable" onClick={() => handleSort('last_contacted_at')}>
                Last Touch <SortIcon col="last_contacted_at" />
              </th>
              <th className="llv__th llv__th--sortable" onClick={() => handleSort('next_follow_up_at')}>
                Follow-up <SortIcon col="next_follow_up_at" />
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td className="llv__empty-cell" colSpan={9}>No leads match your filters</td>
              </tr>
            )}
            {filtered.map(lead => {
              const closed = TERMINAL_STATUSES.includes(lead.status);
              return (
                <tr
                  key={lead._id}
                  className={`llv__row ${closed ? 'llv__row--closed' : ''}`}
                  onClick={() => onCardClick(lead)}
                >
                  <td className="llv__td llv__td--name">
                    {(lead.hot_score || 0) > 70 && <span className="llv__hot">🔥</span>}
                    {lead.name}
                  </td>
                  <td className="llv__td llv__td--mono">{lead.phone}</td>
                  <td className="llv__td">
                    {lead.source ? (
                      <span className="llv__chip llv__chip--source">
                        {SOURCE_LABELS[lead.source] || lead.source}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="llv__td">
                    <span
                      className={`llv__chip llv__chip--status ${closed ? 'llv__chip--closed' : ''}`}
                      style={{ background: STATUS_COLORS[lead.status] + '22', color: STATUS_COLORS[lead.status], borderColor: STATUS_COLORS[lead.status] + '55' }}
                    >
                      {STATUS_LABELS[lead.status] || lead.status}
                    </span>
                  </td>
                  <td className="llv__td">
                    {lead.interest_level ? INTEREST_LABELS[lead.interest_level] : '—'}
                  </td>
                  <td className="llv__td llv__td--mono llv__td--aum">
                    {formatAum(lead.estimated_aum)}
                  </td>
                  <td className="llv__td">
                    <div className="llv__score-wrap">
                      <div className="llv__score-bar">
                        <div className="llv__score-fill" style={{ width: `${lead.hot_score || 0}%` }} />
                      </div>
                      <span className="llv__score-num">{lead.hot_score || 0}</span>
                    </div>
                  </td>
                  <td className="llv__td llv__td--mono">{formatDate(lead.last_contacted_at)}</td>
                  <td className="llv__td llv__td--mono">{formatDate(lead.next_follow_up_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="llv__count">{filtered.length} lead{filtered.length !== 1 ? 's' : ''}</div>
    </div>
  );
}
